import React, { useState, useEffect } from "react";
import {
  FaPlus,
  FaTrash,
  FaArrowLeft,
  FaSave,
  FaEye,
  FaEdit,
} from "react-icons/fa";
import policyService from "../../services/policyService";
import ConfirmationModal from "../../components/UI/ConfirmationModal";
import { useAuth } from "../../contexts/AuthContext";

const CompanyPolicies = () => {
  const { hasPermission } = useAuth();
  const [policies, setPolicies] = useState([]);
  const [selectedPolicy, setSelectedPolicy] = useState(null);
  const [isAdding, setIsAdding] = useState(false);
  const [loading, setLoading] = useState(false);
  const [editingPolicyId, setEditingPolicyId] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [policyToDelete, setPolicyToDelete] = useState(null);

  // Form State
  const [policyName, setPolicyName] = useState("");
  const [sections, setSections] = useState([
    { title: "", points: [{ text: "", subPoints: [] }] },
  ]);

  // Fetch all policies on mount
  useEffect(() => {
    fetchPolicies();
  }, []);

  const fetchPolicies = async () => {
    setLoading(true);
    try {
      const response = await policyService.getPolicies();
      // Adjust based on typical generic API response structures
      const data = response.data || response || [];
      setPolicies(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Error fetching policies:", error);
      alert("Failed to fetch policies");
    } finally {
      setLoading(false);
    }
  };

  const handleViewPolicy = async (id) => {
    setLoading(true);
    try {
      const response = await policyService.getPolicy(id);
      const data = response.data || response;
      setSelectedPolicy(data);
    } catch (error) {
      console.error("Error fetching policy details:", error);
      alert("Failed to fetch policy details");
    } finally {
      setLoading(false);
    }
  };

  const handleEditPolicy = (policy) => {
    setPolicyName(policy.policyName || "");
    const formattedSections =
      (policy.sections || []).length > 0
        ? policy.sections.map((s) => ({
            title: s.title || "",
            points:
              (s.points || []).length > 0
                ? s.points.map((p) => ({
                    text: p.text || "",
                    subPoints: p.subPoints || [],
                  }))
                : [{ text: "", subPoints: [] }],
          }))
        : [{ title: "", points: [{ text: "", subPoints: [] }] }];
    setSections(formattedSections);
    setEditingPolicyId(policy._id || policy.id);
    setSelectedPolicy(null);
    setIsAdding(true);
  };

  const handleDeleteClick = (id) => {
    setPolicyToDelete(id);
    setIsDeleteModalOpen(true);
  };

  const confirmDeletePolicy = async () => {
    if (!policyToDelete) return;
    setLoading(true);
    try {
      await policyService.deletePolicy(policyToDelete);
      setSelectedPolicy(null);
      fetchPolicies();
    } catch (error) {
      console.error("Error deleting policy:", error);
      alert("Failed to delete policy");
    } finally {
      setLoading(false);
      setIsDeleteModalOpen(false);
      setPolicyToDelete(null);
    }
  };

  // Form helpers
  const handleAddSection = () => {
    setSections([
      ...sections,
      { title: "", points: [{ text: "", subPoints: [] }] },
    ]);
  };

  const handleRemoveSection = (index) => {
    if (sections.length > 1) {
      setSections(sections.filter((_, i) => i !== index));
    }
  };

  const handleSectionTitleChange = (index, value) => {
    const newSections = [...sections];
    newSections[index].title = value;
    setSections(newSections);
  };

  const handleAddPoint = (sIndex) => {
    const newSections = [...sections];
    newSections[sIndex].points.push({ text: "", subPoints: [] });
    setSections(newSections);
  };

  const handleRemovePoint = (sIndex, pIndex) => {
    const newSections = [...sections];
    if (newSections[sIndex].points.length > 1) {
      newSections[sIndex].points.splice(pIndex, 1);
      setSections(newSections);
    }
  };

  const handlePointChange = (sIndex, pIndex, value) => {
    const newSections = [...sections];
    newSections[sIndex].points[pIndex].text = value;
    setSections(newSections);
  };

  const handleAddSubPoint = (sIndex, pIndex) => {
    const newSections = [...sections];
    newSections[sIndex].points[pIndex].subPoints.push("");
    setSections(newSections);
  };

  const handleRemoveSubPoint = (sIndex, pIndex, spIndex) => {
    const newSections = [...sections];
    newSections[sIndex].points[pIndex].subPoints.splice(spIndex, 1);
    setSections(newSections);
  };

  const handleSubPointChange = (sIndex, pIndex, spIndex, value) => {
    const newSections = [...sections];
    newSections[sIndex].points[pIndex].subPoints[spIndex] = value;
    setSections(newSections);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!policyName.trim()) {
      alert("Please enter a policy name");
      return;
    }

    setLoading(true);

    try {
      // Clean up empty fields before saving
      const cleanedSections = sections
        .map((s) => ({
          ...s,
          points: s.points
            .map((p) => ({
              text: p.text,
              subPoints: p.subPoints.filter((sp) => sp.trim()),
            }))
            .filter((p) => p.text.trim() || p.subPoints.length > 0),
        }))
        .filter((s) => s.title.trim() || s.points.length > 0);

      const payload = {
        policyName,
        sections: cleanedSections,
      };

      if (editingPolicyId) {
        await policyService.updatePolicy(editingPolicyId, payload);
      } else {
        await policyService.createPolicy(payload);
      }

      setIsAdding(false);
      setEditingPolicyId(null);
      setPolicyName("");
      setSections([{ title: "", points: [{ text: "", subPoints: [] }] }]);

      // Refresh list
      fetchPolicies();
    } catch (error) {
      console.error("Error creating policy:", error);
      alert("Failed to create policy");
    } finally {
      setLoading(false);
    }
  };

  // Utility to handle markdown-like bold inside text (**bold**)
  const formatBold = (text) => {
    if (!text) return "";
    return text.replace(/\*\*(.*?)\*\*/g, "<b>$1</b>");
  };

  const renderPoint = (pointObj) => {
    if (!pointObj || !pointObj.text) return null;

    return (
      <li className="mb-2 text-gray-800 dark:text-gray-200">
        <span dangerouslySetInnerHTML={{ __html: formatBold(pointObj.text) }} />
        {pointObj.subPoints && pointObj.subPoints.length > 0 && (
          <ul className="list-none ml-6 mt-1 space-y-1">
            {pointObj.subPoints.map((subPoint, idx) => (
              <li
                key={idx}
                className="flex gap-2 text-gray-700 dark:text-gray-300"
              >
                <span className="text-[12px] pt-1">&#9675;</span>
                <span
                  dangerouslySetInnerHTML={{
                    __html: formatBold(subPoint.replace(/^[ o\t*-]+/, "")),
                  }}
                />
              </li>
            ))}
          </ul>
        )}
      </li>
    );
  };

  // Add Mode Render
  if (isAdding) {
    return (
      <div className="w-full">
        <div className="w-full dark:bg-gray-900 rounded p-6 shadow-sm bg-white">
          <div className="flex justify-between items-center mb-6">
            <h2 className="md:text-2xl text-xl font-semibold text-gray-800 dark:text-white">
              {editingPolicyId ? "Edit Policy" : "Add New Policy"}
            </h2>
            <button
              onClick={() => {
                setIsAdding(false);
                setEditingPolicyId(null);
                setPolicyName("");
                setSections([
                  { title: "", points: [{ text: "", subPoints: [] }] },
                ]);
              }}
              className="flex items-center gap-2 px-4 py-2 text-gray-600 bg-gray-100 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700 rounded transition"
            >
              <FaArrowLeft size={14} /> Back
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Policy Name
              </label>
              <input
                type="text"
                placeholder="e.g. Leave Policy Conditions"
                value={policyName}
                onChange={(e) => setPolicyName(e.target.value)}
                className="w-full p-2 border border-gray-300 dark:border-gray-700 rounded bg-white dark:bg-black text-gray-800 dark:text-white focus:ring-2 focus:ring-blue-500 outline-none"
                required
              />
            </div>

            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-medium text-gray-800 dark:text-white">
                  Sections
                </h3>
              </div>
              <p className="text-sm text-gray-500 max-w-2xl mb-4">
                Tip: You can wrap any text with "**" to make it <b>**bold**</b>.
                Use the "Add Sub-point" button below any point to easily nest
                related ideas.
              </p>

              {sections.map((section, sIndex) => (
                <div
                  key={sIndex}
                  className="p-5 border border-gray-200 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-800/50 relative"
                >
                  <div className="flex justify-between items-start mb-6 gap-4">
                    <div className="flex-1">
                      <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">
                        Section {sIndex + 1} Title
                      </label>
                      <input
                        type="text"
                        placeholder="e.g. Privilege/Earned Leave (PL)"
                        value={section.title}
                        onChange={(e) =>
                          handleSectionTitleChange(sIndex, e.target.value)
                        }
                        className="w-full p-2 border border-gray-300 dark:border-gray-700 rounded bg-white dark:bg-black text-gray-800 dark:text-white focus:ring-2 focus:ring-blue-500 outline-none font-medium"
                      />
                    </div>
                    {sections.length > 1 && (
                      <button
                        type="button"
                        onClick={() => handleRemoveSection(sIndex)}
                        className="mt-6 p-2 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition"
                        title="Remove Section"
                      >
                        <FaTrash />
                      </button>
                    )}
                  </div>

                  <div className="space-y-4">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Points
                    </label>
                    {section.points.map((point, pIndex) => (
                      <div
                        key={pIndex}
                        className="p-4 border border-gray-100 dark:border-gray-700/60 rounded bg-white dark:bg-gray-900 shadow-sm"
                      >
                        <div className="flex gap-3 items-start">
                          <div className="mt-2 text-gray-400">●</div>
                          <textarea
                            placeholder="Point description..."
                            value={point.text}
                            onChange={(e) =>
                              handlePointChange(sIndex, pIndex, e.target.value)
                            }
                            className="flex-1 p-2 border border-gray-300 dark:border-gray-700 rounded bg-gray-50 dark:bg-black text-gray-800 dark:text-white focus:ring-2 focus:ring-blue-500 outline-none resize-y min-h-[44px]"
                            rows={1}
                            onInput={(e) => {
                              e.target.style.height = "auto";
                              e.target.style.height =
                                e.target.scrollHeight + "px";
                            }}
                          />
                          {section.points.length > 1 && (
                            <button
                              type="button"
                              onClick={() => handleRemovePoint(sIndex, pIndex)}
                              className="p-2 mt-1 text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition"
                              title="Remove Point"
                            >
                              <FaTrash size={12} />
                            </button>
                          )}
                        </div>

                        <div className="ml-8 mt-3 space-y-2">
                          {point.subPoints.map((subPoint, spIndex) => (
                            <div
                              key={spIndex}
                              className="flex gap-2 items-start"
                            >
                              <div className="mt-2 text-[10px] text-gray-400">
                                ○
                              </div>
                              <input
                                type="text"
                                placeholder="Sub-point description..."
                                value={subPoint}
                                onChange={(e) =>
                                  handleSubPointChange(
                                    sIndex,
                                    pIndex,
                                    spIndex,
                                    e.target.value,
                                  )
                                }
                                className="flex-1 p-1.5 px-3 border border-gray-200 dark:border-gray-700 rounded bg-gray-50 dark:bg-black text-sm text-gray-800 dark:text-gray-200 focus:ring-1 focus:ring-blue-500 outline-none"
                              />
                              <button
                                type="button"
                                onClick={() =>
                                  handleRemoveSubPoint(sIndex, pIndex, spIndex)
                                }
                                className="p-1.5 mt-0.5 text-gray-400 hover:text-red-500 transition"
                                title="Remove Sub-point"
                              >
                                <FaTrash size={11} />
                              </button>
                            </div>
                          ))}
                          <button
                            type="button"
                            onClick={() => handleAddSubPoint(sIndex, pIndex)}
                            className="flex items-center gap-1 text-xs text-blue-500 hover:text-blue-600 dark:text-blue-400 dark:hover:text-blue-300 mt-2 font-medium bg-blue-50 dark:bg-blue-900/20 px-2 py-1.5 rounded"
                          >
                            <FaPlus size={9} /> Add Sub-point
                          </button>
                        </div>
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={() => handleAddPoint(sIndex)}
                      className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 mt-3 font-medium px-1"
                    >
                      <FaPlus size={10} /> Add Another Point
                    </button>
                  </div>
                </div>
              ))}
            </div>

            <div className="flex justify-start">
              <button
                type="button"
                onClick={handleAddSection}
                className="flex items-center gap-2 px-4 py-2 border border-blue-500 text-blue-600 hover:bg-blue-50 dark:text-blue-400 dark:hover:bg-blue-900/40 rounded transition"
              >
                <FaPlus size={14} /> Add Another Section
              </button>
            </div>

            <div className="flex justify-end pt-4 border-t border-gray-200 dark:border-gray-700">
              <button
                type="submit"
                disabled={loading}
                className="flex items-center gap-2 px-6 py-2 bg-blue-600 text-white hover:bg-blue-700 rounded transition font-medium disabled:opacity-70"
              >
                {loading ? (
                  "Saving..."
                ) : (
                  <>
                    <FaSave />{" "}
                    {editingPolicyId ? "Update Policy" : "Save Policy"}
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  }

  // Details View Render
  if (selectedPolicy) {
    return (
      <div className="w-full">
        <div className="w-full dark:bg-gray-900 rounded p-6 shadow-sm bg-white min-h-[80vh]">
          <div className="flex justify-between items-center mb-6 pb-4 border-b border-gray-200 dark:border-gray-700">
            <h2 className="md:text-2xl text-xl font-semibold text-gray-800 dark:text-white truncate pr-4">
              {selectedPolicy.policyName}
            </h2>
            <div className="flex gap-2">
              {hasPermission("policy_mgmt_update") && (
                <button
                  onClick={() => handleEditPolicy(selectedPolicy)}
                  className="flex items-center gap-2 px-4 py-2 text-blue-600 bg-blue-50 hover:bg-blue-100 dark:bg-blue-900/20 dark:text-blue-400 dark:hover:bg-blue-900/40 rounded transition whitespace-nowrap"
                >
                  <FaEdit size={14} /> Edit
                </button>
              )}
              {hasPermission("policy_mgmt_delete") && (
                <button
                  onClick={() =>
                    handleDeleteClick(selectedPolicy._id || selectedPolicy.id)
                  }
                  className="flex items-center gap-2 px-4 py-2 text-red-600 bg-red-50 hover:bg-red-100 dark:bg-red-900/20 dark:text-red-400 dark:hover:bg-red-900/40 rounded transition whitespace-nowrap"
                >
                  <FaTrash size={14} /> Delete
                </button>
              )}
              <button
                onClick={() => setSelectedPolicy(null)}
                className="flex items-center gap-2 px-4 py-2 text-gray-600 bg-gray-100 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700 rounded transition whitespace-nowrap"
              >
                <FaArrowLeft size={14} /> Back
              </button>
            </div>
          </div>

          <div className="max-w-4xl mx-auto bg-gray-50/50 dark:bg-black/20 p-8 sm:p-12 border border-gray-200 dark:border-gray-800 rounded-lg shadow-sm">
            <h1 className="text-xl md:text-2xl text-center font-bold text-gray-900 dark:text-white mb-10 pb-4 inline-block w-full border-b border-gray-200 dark:border-gray-800">
              {selectedPolicy.policyName}
            </h1>

            <div className="space-y-8">
              {selectedPolicy.sections && selectedPolicy.sections.length > 0 ? (
                selectedPolicy.sections.map((section, sIndex) => (
                  <div key={sIndex} className="policy-section">
                    <h2 className="text-[17px] font-bold text-gray-900 dark:text-white mb-4">
                      {sIndex + 1}. {section.title}
                    </h2>
                    <ul className="list-disc ml-8 space-y-2 text-[15px]">
                      {section.points &&
                        section.points.map((point, pIndex) => (
                          <React.Fragment key={pIndex}>
                            {renderPoint(point)}
                          </React.Fragment>
                        ))}
                    </ul>
                  </div>
                ))
              ) : (
                <p className="text-gray-500 dark:text-gray-400 text-center py-4">
                  No sections found in this policy.
                </p>
              )}
            </div>

            <div className="mt-12 pt-4 flex justify-between items-center border-t border-gray-200 dark:border-gray-800">
              <span className="text-xs text-gray-500 dark:text-gray-500">
                Last Updated:{" "}
                {selectedPolicy.lastUpdated ||
                  new Date(selectedPolicy.updatedAt || selectedPolicy.createdAt)
                    ?.toISOString()
                    .split("T")[0] ||
                  "N/A"}
              </span>
            </div>
          </div>
        </div>

        {/* Delete Confirmation Modal */}
        <ConfirmationModal
          isOpen={isDeleteModalOpen}
          onClose={() => setIsDeleteModalOpen(false)}
          onConfirm={confirmDeletePolicy}
          message="Are you sure you want to delete this policy?"
        />
      </div>
    );
  }

  // Initial Card List Render
  return (
    <div className="w-full">
      <div className="w-full dark:bg-gray-900 rounded p-6 shadow-sm bg-white min-h-[80vh]">
        <div className="flex justify-between items-center mb-8 pb-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="md:text-2xl text-xl font-semibold text-gray-800 dark:text-white">
            Company Policies
          </h2>
          {hasPermission("policy_mgmt_create") && (
            <button
              onClick={() => {
                setIsAdding(true);
                setEditingPolicyId(null);
                setPolicyName("");
                setSections([
                  { title: "", points: [{ text: "", subPoints: [] }] },
                ]);
              }}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white hover:bg-blue-700 rounded transition font-medium shadow-sm"
            >
              <FaPlus size={14} /> Add Policy
            </button>
          )}
        </div>

        {loading ? (
          <div className="text-center py-10 text-gray-500 dark:text-gray-400 flex justify-center items-center h-40">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        ) : policies.length === 0 ? (
          <div className="text-center py-10 text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-dashed border-gray-200 dark:border-gray-700">
            No policies found. Click "Add Policy" to create one.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {policies.map((policy) => (
              <div
                key={policy._id || policy.id}
                className="bg-gray-50 dark:bg-gray-800/80 p-6 border border-gray-200 dark:border-gray-700 rounded-xl hover:shadow-md transition-all cursor-pointer group flex flex-col justify-between h-full"
                onClick={() => handleViewPolicy(policy._id || policy.id)}
              >
                <div>
                  <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/40 text-blue-600 dark:text-blue-400 rounded-lg flex items-center justify-center mb-4">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                      <path d="M14 3v4a1 1 0 0 0 1 1h4" />
                      <path d="M17 21h-10a2 2 0 0 1 -2 -2v-14a2 2 0 0 1 2 -2h7l5 5v11a2 2 0 0 1 -2 2z" />
                      <path d="M9 9h1" />
                      <path d="M9 13h6" />
                      <path d="M9 17h6" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2 line-clamp-2 leading-tight">
                    {policy.policyName}
                  </h3>
                </div>

                <div className="mt-6 pt-4 border-t border-gray-200 dark:border-gray-700 flex justify-between items-center group-hover:border-blue-200 dark:group-hover:border-blue-800/50 transition-colors">
                  <span className="text-xs text-gray-400 dark:text-gray-500">
                    {new Date(
                      policy.updatedAt ||
                        policy.createdAt ||
                        policy.lastUpdated ||
                        Date.now(),
                    ).toLocaleDateString()}
                  </span>
                  <span className="text-sm font-medium text-blue-600 dark:text-blue-400 flex items-center gap-1 opacity-0 transform translate-x-3 group-hover:opacity-100 group-hover:translate-x-0 transition-all">
                    View <FaEye size={12} />
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={confirmDeletePolicy}
        message="Are you sure you want to delete this policy?"
      />
    </div>
  );
};

export default CompanyPolicies;
